/**
 * Quem é a pessoa, lido do `id_token` — para as telas em que a identidade foi
 * confirmada e o acesso, não (login, telas 7 e 8).
 *
 * Nessas telas ainda não existe `/v1/me`: é justamente ele que recusou. O que
 * há é o token do provedor, e ele traz nome e e-mail. Ler daqui é o único
 * jeito de a tela dizer "você, Ricardo, foi confirmado" em vez de "alguém".
 *
 * Só leitura de claims, sem verificar assinatura: o token já foi aceito pelo
 * provedor e pela API; aqui ele serve para mostrar, não para autorizar.
 */

export interface PessoaDoToken {
  readonly nome: string;
  readonly apoio: string;
  readonly iniciais: string;
}

function claimsDe(token: string): Record<string, unknown> {
  const corpo = token.split('.')[1];
  if (!corpo) return {};
  try {
    const json = atob(corpo.replace(/-/g, '+').replace(/_/g, '/'));
    const claims: unknown = JSON.parse(json);
    return claims !== null && typeof claims === 'object' ? (claims as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

/** "Ricardo Nunes" → "RN"; "ricardo.nunes@x" → "R"; vazio → "?". */
export function iniciaisDe(nome: string): string {
  const partes = nome
    .trim()
    .split(/\s+/)
    .filter((p) => p !== '' && !p.includes('@'));
  if (partes.length === 0) return nome.trim() === '' ? '?' : nome.trim()[0]!.toUpperCase();
  const primeira = partes[0]![0] ?? '';
  const ultima = partes.length > 1 ? (partes[partes.length - 1]![0] ?? '') : '';
  return `${primeira}${ultima}`.toUpperCase();
}

/**
 * A pessoa, ou nada — a tela mostra o bloco só quando há o que mostrar.
 *
 * O nome vem de `name`; sem ele, o e-mail vira o nome (é o que a pessoa
 * reconhece), e o apoio diz de onde a confirmação veio.
 */
export function pessoaDoToken(idToken: string | null): PessoaDoToken | null {
  if (!idToken) return null;
  const claims = claimsDe(idToken);
  const nome = typeof claims['name'] === 'string' ? claims['name'].trim() : '';
  const email = typeof claims['email'] === 'string' ? claims['email'].trim() : '';
  const usuario =
    typeof claims['preferred_username'] === 'string' ? claims['preferred_username'].trim() : '';
  const principal = nome || email || usuario;
  if (principal === '') return null;
  return {
    nome: principal,
    apoio: nome && email ? email : 'confirmado no provedor de identidade',
    iniciais: iniciaisDe(principal),
  };
}
